/**
 * Per-user tooltip preferences. Admin group toggles are the ceiling — these
 * only let a user opt out of what admins have enabled.
 *
 * Storage is two-tier: localStorage is the synchronous source the UI reads,
 * and `profiles.ui_prefs` is the durable copy that follows the account across
 * sessions and devices. Signed-out visitors stay purely local.
 *
 * Shape:
 *   { tips: boolean, tour: boolean, groups: { [groupKey]: { tour, tips } } }
 * Legacy shapes (a single `enabled` master flag, or a bare group map) are
 * still read so existing users are not disrupted.
 */
import { useSyncExternalStore } from "react";
import { supabase } from "@/integrations/supabase/client";

export type TooltipPrefs = { tour: boolean; tips: boolean };

const KEY = "podgo.tooltips.prefs";
const DEFAULTS: TooltipPrefs = { tour: true, tips: true };

type Store = {
  /** Global hover/focus tips switch. */
  tips: boolean;
  /** Global guided walkthrough switch. */
  tour: boolean;
  groups: Record<string, Partial<TooltipPrefs>>;
};

const listeners = new Set<() => void>();
let version = 0;

function emit() {
  version += 1;
  listeners.forEach((l) => l());
}


function read(): Store {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "{}") as Partial<Store> & {
      enabled?: boolean;
    } & Record<string, unknown>;
    if (raw && typeof raw === "object" && "groups" in raw) {
      // Legacy single master flag maps onto both switches.
      const legacy = raw.enabled !== false;
      return {
        tips: raw.tips ?? legacy,
        tour: raw.tour ?? legacy,
        groups: (raw.groups as Store["groups"]) || {},
      };
    }
    // Oldest shape: the whole object was the group map.
    return { tips: true, tour: true, groups: (raw as Store["groups"]) || {} };
  } catch {
    return { tips: true, tour: true, groups: {} };
  }
}

function write(store: Store, { push = true }: { push?: boolean } = {}) {
  localStorage.setItem(KEY, JSON.stringify(store));
  if (push) schedulePush(store);
  emit();
}

// --- Remote sync (profiles.ui_prefs) --------------------------------------

/** Set once the account's stored prefs have been merged in (or no account). */
let hydrated = false;
let hydratedFor: string | null = null;
let syncUserId: string | null = null;
let pushTimer: ReturnType<typeof setTimeout> | undefined;

function schedulePush(store: Store) {
  if (!syncUserId) return;
  const userId = syncUserId;
  clearTimeout(pushTimer);
  // Debounced: toggling several switches in a row is one write.
  pushTimer = setTimeout(() => {
    void supabase
      .from("profiles")
      .update({ ui_prefs: store } as never)
      .eq("id", userId);
  }, 800);
}

/** A dismissal on any device wins, so `false` is sticky when merging. */
function mergePrefs(a?: Partial<TooltipPrefs>, b?: Partial<TooltipPrefs>): Partial<TooltipPrefs> {
  const out: Partial<TooltipPrefs> = { ...a, ...b };
  (["tour", "tips"] as const).forEach((k) => {
    if (a?.[k] === false || b?.[k] === false) out[k] = false;
  });
  return out;
}

function mergeStores(local: Store, remote: Partial<Store>): Store {
  const groups: Store["groups"] = { ...local.groups };
  Object.entries(remote.groups || {}).forEach(([key, value]) => {
    groups[key] = mergePrefs(local.groups[key], value);
  });
  const merged = mergePrefs(
    { tips: local.tips, tour: local.tour },
    { tips: remote.tips, tour: remote.tour },
  );
  return { tips: merged.tips ?? true, tour: merged.tour ?? true, groups };
}

/**
 * Pull the account's stored prefs and merge them with whatever this device
 * already knows. Safe to call repeatedly; only re-runs when the user changes.
 */
export async function hydrateTooltipPrefs(userId: string | null | undefined) {
  syncUserId = userId ?? null;
  if (!userId) {
    hydratedFor = null;
    hydrated = true;
    emit();
    return;
  }
  if (hydratedFor === userId) return;
  hydratedFor = userId;
  const { data } = await supabase
    .from("profiles")
    .select("ui_prefs")
    .eq("id", userId)
    .maybeSingle();
  const remote = ((data as { ui_prefs?: Partial<Store> } | null)?.ui_prefs ?? {}) as Partial<Store>;
  const merged = mergeStores(read(), remote);
  // Persist the merge back so both sides converge on the same object.
  write(merged);
  hydrated = true;
  emit();
}

/** True once remote prefs are known — gate auto-starting tours on this. */
export function areTooltipPrefsHydrated() {
  return hydrated;
}


export function getTooltipPrefs(groupKey: string): TooltipPrefs {
  return { ...DEFAULTS, ...(read().groups[groupKey] || {}) };
}

export function setTooltipPrefs(groupKey: string, patch: Partial<TooltipPrefs>) {
  const store = read();
  store.groups[groupKey] = { ...DEFAULTS, ...(store.groups[groupKey] || {}), ...patch };
  write(store);
}

/** Global switches — hover tips and guided tours are independent. */
export function getGlobalTooltipPrefs(): TooltipPrefs {
  const { tips, tour } = read();
  return { tips, tour };
}

export function setGlobalTooltipPref(patch: Partial<TooltipPrefs>) {
  write({ ...read(), ...patch });
}

/** Clear every dismissal so walkthroughs replay, and turn both switches back on. */
export function resetTooltipPrefs() {
  write({ tips: true, tour: true, groups: {} });
}

// --- React bindings --------------------------------------------------------

function subscribe(cb: () => void) {
  listeners.add(cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) emit();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(cb);
    window.removeEventListener("storage", onStorage);
  };
}

/** Bumps on every preference write (this tab or another). */
export function useTooltipPrefsVersion(): number {
  return useSyncExternalStore(
    subscribe,
    () => version,
    () => 0,
  );
}

/** Live global switches, safe to read during SSR/tests. */
export function useGlobalTooltipPrefs(): TooltipPrefs {
  // Version-keyed read keeps the snapshot referentially stable between writes.
  useTooltipPrefsVersion();
  return typeof window === "undefined" ? DEFAULTS : getGlobalTooltipPrefs();
}

/** Live hydration flag — false until the account's stored prefs are merged. */
export function useTooltipPrefsHydrated(): boolean {
  useTooltipPrefsVersion();
  return areTooltipPrefsHydrated();
}

